import { isInternalOrg, useAnalytics } from 'src/composables/useAnalytics'
import { PublicClientApplication } from '@azure/msal-browser'
import { defineStore, getActivePinia } from 'pinia'
import { clearLocalStorageExcept } from 'src/config/auth-config'
import { roles } from 'src/config/roles-config.js'
import router from 'src/router'
import AuthService from 'src/services/auth'
import { isUserAssignedToOrg } from 'src/utils/auth'
import { customErrorHandler, defaultError, handleError } from 'src/utils/error'
import { useToast } from 'vue-toastification'

const toast = useToast()
const store = getActivePinia()

// Backend may return alternate module codes depending on environment.
// Map them to the canonical codes used in src/config/modules.js so every
// orgModules consumer (sidebar, route guard, home cards, modals) works.
const MODULE_CODE_ALIASES = {
  station_design: 'station_improvement',
  quality_planning: 'fmea'
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isSidebarOpen: true,
    isMobileDrawerOpen: false,
    user: localStorage.getItem('name'),
    token: localStorage.getItem('token'),
    refresh: null,
    isTokenSet: !!localStorage.getItem('token'),
    organization: localStorage.getItem('organization'),
    organizationDisplayName: localStorage.getItem('organization-name-to-display'),
    role: localStorage.getItem('role'),
    hostURL: null,
    poc: null,
    isPolicyAccepted: null,
    accessToken: null,
    msalInstance: null,
    azureUser: false,
    isOtpSetup: null,
    email: localStorage.getItem('email'),
    requireOtp: null,
    isSigningIn: false,
    AWSCustomerIdentifier: null,
    sessionKey: '',
    linkedOrganizations: [],
    organizationDescription: localStorage.getItem('organizationDescription'),
    orgStatus: localStorage.getItem('orgStatus'),
    // Customer group above organization (e.g. '3M'), only present for some orgs.
    parentCompany: localStorage.getItem('parentCompany'),
    receiveOtpOn: null,
    orgModules: null
  }),
  actions: {
    setUser(value) {
      this.user = value
    },
    setToken(value) {
      this.token = value
      this.isTokenSet = value ? true : false
    },
    setOrganization(value) {
      this.organization = value
    },
    setOrganizationDisplayName(value) {
      this.organizationDisplayName = value
    },
    setOrganizationDescription(value) {
      this.organizationDescription = value
    },
    setOrgStatus(value) {
      this.orgStatus = value
    },
    setParentCompany(value) {
      this.parentCompany = value
    },
    setRole(value) {
      this.role = value
    },
    setIsPolicyAccepted(value) {
      this.isPolicyAccepted = value
    },
    setEmail(value) {
      this.email = value
    },
    setIsSidebarOpen(value) {
      this.isSidebarOpen = value
    },
    setIsMobileDrawerOpen(value) {
      this.isMobileDrawerOpen = value
    },
    setMsalInstance(value) {
      this.msalInstance = value
    },
    setAWSCustomerIdentifier(value) {
      this.AWSCustomerIdentifier = value
    },
    setSessionKey(value) {
      this.sessionKey = value
    },
    setReceiveOtpOn(value) {
      this.receiveOtpOn = value
    },
    setOtpSetup(value) {
      this.isOtpSetup = value
    },
    setRequireOtp(value) {
      this.requireOtp = value
    },
    setAzureUser(value) {
      this.azureUser = value
    },
    setMfaMethod(receive_otp_on) {
      window.localStorage.setItem('receiveOtpOn', receive_otp_on)
      this.setReceiveOtpOn(receive_otp_on)
    },
    async createMsalInstance() {
      const msalConfig = {
        auth: {
          clientId: 'c5093c91-21ae-4374-9be1-d95791ea78eb',
          authority: 'https://login.microsoftonline.com/organizations'
        },
        cache: {
          cacheLocation: 'localStorage'
        }
      }
      this.msalInstance = new PublicClientApplication(msalConfig)
    },

    async azureSignIn(payload, username) {
      const [error, data] = await AuthService.azureSignIn(payload)
      if (error) {
        toast.error(error.response?.data?.response || defaultError)
        this.clearAllStates()
        localStorage.clear()
        sessionStorage.clear()
        this.isOtpSetup = false
        this.requireOtp = true
        return
      }

      if (data.response === 'User created successfully') {
        setTimeout(toast.success(data.response), 3000)
        this.clearAllStates()
        localStorage.clear()
        sessionStorage.clear()
        this.isOtpSetup = false
        this.requireOtp = true
        return
      } else {
        if (isUserAssignedToOrg(data)) {
          return toast.error('User is not assigned to any organization')
        }
      }
      this.setUserDetails({ ...data, email: data.email || username })
      this.fetchOrganizations()
      localStorage.setItem('azureUser', true)
    },

    async userLogin(payload) {
      this.isSigningIn = true
      const [error, data] = await AuthService.signIn(payload)

      if (error) {
        handleError(error?.response, 'login-error-toast')
        this.isSigningIn = false
        return false
      }
      const { aws_customer_identifier } = data
      if (!aws_customer_identifier) {
        if (isUserAssignedToOrg(data)) {
          if (error?.response?.status === 429) {
            toast.error('Too many attempts.Please try after 1 min.')
            return false
          }
          return handleError(error?.response, 'login-error-toast')
        }
      }
      localStorage.setItem('azureUser', false)
      let { token } = data
      if (!token) token = payload.token

      console.log('data',data)
      this.setUserDetails({ ...data, email: payload.email })
      if (!data.require_otp) {
        await this.fetchOrganizations()
        this.isSigningIn = false
        return true
      }
      this.isSigningIn = false
      return true
    },

    setUserDetails(data) {
      const {
        id,
        name,
        email,
        role,
        token,
        Organization,
        poc,
        is_policy_accepted,
        is_otp_verified,
        require_otp,
        refresh,
        session_key,
        aws_customer_identifier,
        receive_otp_on
      } = data
      const username = role === roles.admin ? 'Andrey Konin' : name
      const org = role === roles.admin ? 'ADMINISTRATOR' : Organization
      const org_description = data?.Config?.user_id?.Description // organization description
      const org_display_name = data?.Config?.user_id?.org_name_to_display // organization display name
      const org_status = data?.Config?.user_id?.org_status // internal/test-org marker, see docs/Behavioral_Analysis_Mixpanel.md §3b
      const parent_company = data?.Config?.user_id?.parent_company // customer group above organization, see docs/Behavioral_Analysis_Mixpanel.md §1
      const currentTime = new Date()
      window.localStorage.setItem('id', id)
      window.localStorage.setItem('name', username)
      window.localStorage.setItem('email', email)
      window.localStorage.setItem('role', role)
      // window.localStorage.setItem('organization', org)
      window.localStorage.setItem('token', token)
      window.localStorage.setItem('refresh', refresh)
      window.localStorage.setItem('timestamp', currentTime)
      window.localStorage.setItem('poc', poc)
      window.localStorage.setItem('isPolicyAccepted', is_policy_accepted)
      window.localStorage.setItem('sessionKey', session_key)
      window.localStorage.setItem('AWSCustomerIdentifier', aws_customer_identifier)
      window.localStorage.setItem('organizationDescription', org_description)
      window.localStorage.setItem('organizationDisplayName', org_display_name)
      window.localStorage.setItem('orgStatus', org_status)
      window.localStorage.setItem('receiveOtpOn', receive_otp_on)
      // Not unconditional like the fields above — localStorage stringifies null/undefined
      // to "null"/"undefined", which would read back as a truthy string on reload and break
      // the "send only when non-null" rule. Absence (never set / explicitly removed) always
      // reads back as real null.
      if (parent_company) {
        window.localStorage.setItem('parentCompany', parent_company)
      } else {
        window.localStorage.removeItem('parentCompany')
      }

      console.log('ORG', org)

      this.setUser(username)
      this.setEmail(email)
      this.setToken(token)
      this.setOrganization(org)
      this.setOrganizationDescription(org_description)
      this.setOrgStatus(org_status)
      this.setParentCompany(parent_company ?? null)
      this.setRole(role)
      this.setRequireOtp(require_otp)
      this.setOtpSetup(is_otp_verified)
      this.setIsPolicyAccepted(is_policy_accepted)
      this.setAWSCustomerIdentifier(aws_customer_identifier)
      this.setSessionKey(session_key)
      this.setReceiveOtpOn(receive_otp_on)
      this.setOrganizationDisplayName(org_display_name)

      // Mixpanel identity + login event — fired after the store state above is set,
      // so track()'s base properties (organization, role) are accurate rather than
      // stale from a previous session. Skipped entirely for internal/test orgs in
      // production; see docs/Behavioral_Analysis_Mixpanel.md §3b and §2.
      if (!isInternalOrg(org_status)) {
        const { identify, setSuperProperties, track } = useAnalytics()
        identify(id, { $name: username, $email: email, organization: org, role })
        setSuperProperties({ id, name: username, role })
        // Satisfies Mixpanel's requirement of at least one track() call after
        // identify() for the anonymous-to-known merge to take effect, and doubles
        // as a login funnel step.
        track('Login')
      }
    },

    clearState() {
      if (this.isTokenSet) {
        AuthService.logout()
      }
      this.clearAllStates()
      // localStorage.clear()
      sessionStorage.clear()
    },

    clearAllStates() {
      this.setUser(null)
      this.setEmail(null)
      this.setToken(null)
      this.setOrganization(null)
      this.setRole(null)
      this.setIsPolicyAccepted(null)
      this.setSessionKey(null)
      this.setAWSCustomerIdentifier(null)
      this.setOrganizationDescription(null)
      this.setOrganizationDisplayName(null)
      this.setOrgStatus(null)
    },

    async userLogout() {
      if (this.isTokenSet) {
        await AuthService.logout()
      }
      //  clear all keys from localStorage except that keep track for Demo functionality
      store?._s?.forEach((store) => store?.$reset())
      this.clearAllStates()
      clearLocalStorageExcept()
      sessionStorage.clear()
      const { reset } = useAnalytics()
      reset()
    },

    async forgotPassword(payload) {
      const [error, data] = await AuthService.forgotPassword(payload)
      if (error) return customErrorHandler(error?.response, 'forgot-password-error-toast')
      toast.success(data.response)
      router.replace({ name: 'Login' }, 3000)
    },

    async changePassword(payload) {
      const [error, data] = await AuthService.changePassword(payload)
      if (error) return customErrorHandler(error?.response)
      toast.success(data.response)
      router.replace({ name: 'Login' }, 3000)
    },

    async resendOTP() {
      const [error] = await AuthService.resendOtp()
      if (error) {
        if (error?.repsonse?.data) customErrorHandler(error?.response)
        else toast.error('Error occurred, Please try login again!')
        return
      }
      toast.success('OTP Sent!')
    },

    async verifyOTP(payload) {
      const [error] = await AuthService.verifyOTP(payload)
      if (error) {
        customErrorHandler(error?.response)
        return false
      }
      this.fetchOrganizations()
      return true
    },

    async fetchOrganizations(selectOnlyOrg = true) {
      let params = { user_id: localStorage.getItem('id'), sorted_list: true }
      const [error, data] = await AuthService.getUserLinkedOrgByUserId(params)
      if (error) {
        customErrorHandler(error?.response)
        return
      }
      if (data.length == 1 && selectOnlyOrg) {
        let payload = {
          organization: data[0].organization,
          orgDisplayName: data[0].organization_name_to_display
        }
        await this.selectOrganization(payload)
      }
      this.linkedOrganizations = data
    },

    async selectOrganization(payload) {
      const [error, data] = await AuthService.switchOrganizationByUser({ organization: payload.organization })
      if (error) {
        return error
      }
      this.setOrganizationDescription(data?.description)

      localStorage.setItem('organizationDescription', data?.description)
      localStorage.setItem('organization', payload.organization)
      localStorage.setItem('organizationDisplayName', payload?.orgDisplayName)

      this.organization = payload.organization
      await this.fetchOrgModules()
      router.replace({ path: '/' })
    },

    async setOtpMethodEmail(mfaMethod) {
      const [error, data] = await AuthService.setOtpMethodEmail()
      if (error) {
        handleError(error?.response)
        return
      }
      toast.success(data.response)
      this.setMfaMethod(mfaMethod)
    },

    async fetchOrgModules() {
      const [error, data] = await AuthService.getOrgModules()
      if (error) {
        this.orgModules = null
        return
      }
      this.orgModules = (data?.modules || []).map((m) => ({
        ...m,
        code: MODULE_CODE_ALIASES[m.code] || m.code
      }))
    },

    async setOtpMethod(payload, mfaMethod, isUserLoggingIn) {
      if (payload.OTP.length != 6) {
        toast.error('Please enter the 6-digit OTP')
        return false
      }
      const [error, data] = await (mfaMethod === 'sms'
        ? AuthService.setOtpMethodSms(payload)
        : AuthService.setOtpMethodAuthApp(payload))

      if (error) {
        customErrorHandler(error?.response)
        return false
      }

      toast.success(data.response)
      this.setMfaMethod(mfaMethod)
      if (isUserLoggingIn) {
        this.fetchOrganizations()
      }
      return true
    }
  }
})
